import HighlightOff from "@mui/icons-material/HighlightOff"
import { useMediaQuery, useTheme } from "@mui/material"
import React, { FC, useEffect } from "react"

import { Accordion } from "src/components/common/accordion"
import { CardTable } from "src/components/common/cardTable"
import { CollapseableSection } from "src/components/common/collapseableSection/CollapseableSection"
import { Table, TableData } from "src/components/common/table"

import { create } from "src/helpers/bem"

import { fetchCoExhibitors } from "src/helpers/fetchQueriesXRM"
import {
  BookingCoExhibitor,
  BookingCoExhibitorChildren,
  useBooking,
} from "src/states/bookingDetail"
import { mergeCoExhibitorDelete } from "src/states/coExhibitorDelete"
import { useLocale } from "src/states/locale"

import { addNotification } from "src/states/notifications"
import { TranslationMessages } from "src/translations"

import styles from "./BookingDetailCoExhibitorTable.module.scss"

const bem = create(styles, "BookingDetailCoExhibitorTable")

export type BookingDetailCoExhibitorTableProps = {
  messages: TranslationMessages
  isActiveBooking: boolean
}

export const BookingDetailCoExhibitorTable: FC<
  BookingDetailCoExhibitorTableProps
> = ({ isActiveBooking, messages }) => {
  const m = messages.pages.booking.detail.coExhibitors
  const locale = useLocale()
  const { booking, coExhibitors } = useBooking()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"))

  useEffect(() => {
    fetchCoExhibitors(booking?.id ?? "")
  }, [booking?.id, locale])

  const handleSearchTable = (value: string, data: TableData[]) => {
    const Value = value.toLowerCase()
    return data.filter((item) => {
      if (!Value) return true
      if (
        item.name?.toLowerCase()?.includes(Value) ||
        (item.exhibitor as string)?.toLowerCase()?.includes(Value) ||
        (item.exhibition as string)?.toLowerCase()?.includes(Value) ||
        (typeof item?.client === "string" &&
          item?.client?.toLowerCase()?.includes(Value))
      ) {
        return true
      }
    })
  }

  const canDeleteCoexhibitorBooking = () => {
    const endDate = booking?.exhibition?.endDateDeleteCoExhibitor
    if (!endDate) return true
    return Date.now() <= Date.parse(endDate)
  }

  const renderTable = () => (
    <Table
      cellTranslations={m.table}
      data={coExhibitors as TableData[]}
      dataTableTranslations={m.dataTable}
      handleSearch={handleSearchTable}
      messages={messages}
      order="desc"
      orderBy="date"
      iconActions={
        isActiveBooking
          ? [
              {
                icon: <HighlightOff />,
                color: canDeleteCoexhibitorBooking() ? "error" : "info",
                onClick: (e) =>
                  canDeleteCoexhibitorBooking()
                    ? mergeCoExhibitorDelete({
                        open: true,
                        activeUser: e as BookingCoExhibitor,
                      })
                    : addNotification({
                        autoclose: true,
                        variant: "warning",
                        content:
                          messages.pages.booking.detail.actions
                            .deleteCoExhibitor.error,
                      }),
              },
            ]
          : []
      }
    />
  )

  const renderMobileTable = () => {
    return (
      <CardTable
        getKey={(coExhibitors) => coExhibitors.name}
        items={[...coExhibitors] as BookingCoExhibitor[]}
        onDelete={(coExhibitor) =>
          canDeleteCoexhibitorBooking()
            ? mergeCoExhibitorDelete({
                open: true,
                activeUser: coExhibitor as BookingCoExhibitor,
              })
            : addNotification({
                autoclose: true,
                variant: "warning",
                content:
                  messages.pages.booking.detail.actions.deleteCoExhibitor.error,
              })
        }
      >
        {(coExhibitor, index) => {
          const keys = Object.keys(coExhibitor)

          return keys
            .filter((key) => key !== "id")
            .map((key) => {
              if (Array.isArray(coExhibitor[key])) {
                return (
                  <CollapseableSection key={key}>
                    {(coExhibitor[key] as BookingCoExhibitorChildren[]).map(
                      (d) => {
                        return d.items.map((item: any) => {
                          return (
                            <div key={item.name}>
                              <span>
                                {(m.dataTable as any).company.items[item.name]}
                              </span>
                              : <span>{item.value}</span>
                            </div>
                          )
                        })
                      },
                    )}
                  </CollapseableSection>
                )
              }

              const translationValue = (m as any).table[key]
              return (
                <div key={key}>
                  <span>{translationValue.label}</span>:{" "}
                  <span>
                    {!translationValue.items
                      ? coExhibitor[key]
                      : translationValue.items[coExhibitor[key] as string]}
                  </span>
                </div>
              )
            })
        }}
      </CardTable>
    )
  }

  return (
    <div className={bem()}>
      <Accordion
        colorVariant="base"
        id={`${booking?.id}-coExhibitor-accordion`}
        label={m.headline}
      >
        {coExhibitors && coExhibitors.length > 0 ? (
          isMobile ? (
            renderMobileTable()
          ) : (
            renderTable()
          )
        ) : (
          <p>{m.noData}</p>
        )}
      </Accordion>
    </div>
  )
}
